import React from 'react';
import {
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  cardHeadingSx,
  fieldLabelSx,
  invalidOutlinedInputSx,
  invalidTextFieldSx,
  requiredFieldLabelSx,
  validationHelperTextSx,
} from './headerAnalysisRulePresentation';
import {
  HEADER_ANALYSIS_RULE_FIELD_MAX_LENGTH,
  HeaderAnalysisRuleFieldErrors,
} from './headerAnalysisRuleValidation';

interface RuleInformationCardProps {
  ruleName: string;
  status: string;
  description: string;
  fieldErrors: Pick<HeaderAnalysisRuleFieldErrors, 'ruleName' | 'description'>;
  onChange: (patch: {ruleName?: string; status?: string; description?: string}) => void;
  onLengthLimitExceeded: (field: 'ruleName' | 'description') => void;
  onRuleNameBlur: () => void;
}

export const RuleInformationCard: React.FC<RuleInformationCardProps> = ({
  ruleName, status, description, fieldErrors, onChange, onLengthLimitExceeded,
  onRuleNameBlur,
}) => {
  const changeLimitedField = (
    field: 'ruleName' | 'description',
    value: string,
  ) => {
    onChange({[field]: value.slice(0, HEADER_ANALYSIS_RULE_FIELD_MAX_LENGTH)});
    if (value.length > HEADER_ANALYSIS_RULE_FIELD_MAX_LENGTH) {
      onLengthLimitExceeded(field);
    }
  };

  return (
  <Card data-testid="rule-information-card" variant="outlined" sx={{p: 3, height: '100%', boxSizing: 'border-box'}}>
    <Typography className="rule-card-heading" sx={{...cardHeadingSx, mb: 3}}>Rule Information</Typography>
    <Stack spacing={2.5}>
      <Box>
        <InputLabel
          htmlFor="header-analysis-rule-name"
          required
          sx={requiredFieldLabelSx}
        >
          Rule Name
        </InputLabel>
        <TextField
          id="header-analysis-rule-name"
          fullWidth
          required
          size="small"
          placeholder="Enter rule name here"
          value={ruleName}
          onChange={event => changeLimitedField('ruleName', event.target.value)}
          onBlur={onRuleNameBlur}
          error={Boolean(fieldErrors.ruleName)} helperText={fieldErrors.ruleName}
          sx={{
            ...invalidTextFieldSx,
            '& .MuiFormHelperText-root.Mui-error': {
              ...validationHelperTextSx,
            },
          }}
        />
      </Box>
      <Box>
        <Typography component="div" sx={fieldLabelSx}>Status</Typography>
        <FormControlLabel
          sx={{m: 0}}
          control={<Checkbox checked={status === 'active'} onChange={event => onChange({status: event.target.checked ? 'active' : 'inactive'})}/>}
          label="Active"
        />
      </Box>
      <Box>
        <InputLabel
          htmlFor="header-analysis-rule-description"
          required
          sx={requiredFieldLabelSx}
        >
          Description
        </InputLabel>
        <OutlinedInput
          id="header-analysis-rule-description"
          className={'input-field'}
          sx={{
            height: '150px',
            ...invalidOutlinedInputSx,
          }}
          multiline={true}
          fullWidth
          required
          rows={6}
          placeholder='Enter description here'
          value={description}
          onChange={event => changeLimitedField('description', event.target.value)}
          error={Boolean(fieldErrors.description)}
        />
        {fieldErrors.description && (
          <FormHelperText
            error
            sx={validationHelperTextSx}
          >
            {fieldErrors.description}
          </FormHelperText>
        )}
      </Box>
    </Stack>
  </Card>
  );
};
